import React, { useMemo, useRef, useState } from 'react'
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom'
import Layout from 'shared/layouts/Main'
import { Button, Col, Form, Row } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import InputWrapper from 'shared/components/InputWrapper'
import resAddPermission from './resolvers/resAddPermission'
import useInit from './hooks/useInit'
import useFocusError from 'shared/hooks/useFocusError'
import prepareSubmitForm from 'shared/utils/prepareSubmitForm'
import submitForm from './utils/submitForm'
import { apiUrls } from 'consts'
import getBrandContent from 'utils/getBrandContent'
import Footer from 'shared/components/Footer'
import BackButton from 'shared/components/BackButton'
import AddRemoveTableSortable from 'components/AddRemoveTableSortable'
import ModalEditableProps from 'components/EditModal/types/ModalEditableProps'
import prepareSubmitFormFromModal from 'components/EditModal/utils/prepareSubmitFormFromModal'
import Entity from 'components/AddRemoveTableSortable/types/Entity'
import AddSection from 'pages/AddSection'
import usePagination from '../../components/Pagination/hooks/usePagination'
import Pagination from 'components/Pagination'
import { IonIcon } from '@ionic/react'
import { cloudUpload } from 'ionicons/icons'
import RecursiveFilterBlock from 'components/RecursiveFilterBlock'
import Select from 'react-select'
import flattenFilters from './utils/flattenFilters'
import useAutoSelectFilterPage from './hooks/useAutoSelectFilterPage'
import addAllSections from './utils/addAllSections'
import removeAllSections from './utils/removeAllSections'

const submitRef = React.createRef<HTMLButtonElement>()

const AddPermission: React.FunctionComponent<ModalEditableProps> = React.forwardRef(
  (props: ModalEditableProps, ref) => {
    const [showOverlay, setShowOverlay] = useState<boolean>(true)
    const [dataLoaded, setDataLoaded] = useState<boolean>(false)
    const [relationalFilterValues, setRelationalFilterValues] = useState<Array<Entity>>([])
    const [relationalSections, setRelationalSections] = useState<Array<Entity>>([])

    const [currentCustomer, setCurrentCustomer] = useState<{} | null>({})

    const [filters, setFilters] = useState<Array<any>>([])

    const [selectedFilter, setSelectedFilter] = useState<any>(null)

    const { id } = useParams()
    const [searchParams] = useSearchParams();

    const isTemporary : boolean = searchParams.get("temp") === "1"; 

    const isCreate : boolean = id === undefined

    const navigate = useNavigate()

    const [permissionId] = useState<string | undefined>(props.modalConfig?.entityId ?? id)

    const [colAttributes] = useState<any>(props.modalConfig !== undefined ? { md: 12 } : undefined)

    const flattenedFilters = useMemo(() => {
        return flattenFilters(filters)
    }, [filters])

    //isCreate - we render all filters, b/c we do not want to initialize them again (BE request, invert selected...)
    const { currentItems: paginatedFilters, currentPage, totalPages, goToPage } = usePagination(filters, 1, isCreate ? null : selectedFilter)

    const [recentlySyncedFilterValues, setRecentlySyncedFilterValues] = useState<boolean>(false);

    const relatedFilterValuesRef = useRef<Entity[]>([])

    const {selectedFilterAndChildrenIdsFlat} = useAutoSelectFilterPage({
      isCreate,
      selectedFilter,
      filters,
      goToPage,
    })

    const {
      register,
      getValues,
      handleSubmit,
      reset,
      setFocus,
      setError,
      formState: { errors },
    } = useForm({
      mode: 'all',
      resolver: resAddPermission,
    })

    useInit({
      isTemporary,
      getValues,
      setFilters,
      id: permissionId,
      reset,
      setDataLoaded,
      setShowOverlay,
      currentCustomer,
    })

    useFocusError({
      errors,
      setFocus,
    })

    const renderView = (innerContent: any) => {
      return props.modalConfig === undefined ? (
        <Layout
          footerContent={<Footer />}
          brandContent={getBrandContent()}
          showOverlay={showOverlay}
          showCardHeader={true}
          headerInsert={<BackButton url='/permissions' text='All Permissions' />}
          actionInsert={
            <>
              {!isCreate && (
              <NavLink to={'/import-permission-to-filter-values/'+id} className='btn btn-primary me-3'>
              <IonIcon
                icon={cloudUpload}
                className='ionicon'
                style={{ fontSize: '16px', marginBottom: '-3px' }}
              />
              Import Filter Values
            </NavLink>
            )}
            <Button
              disabled={!dataLoaded}
              type='submit'
              name='save'
              className='btn-round btn-primary'
              onClick={() => {
                if (submitRef.current != null) {
                  submitRef.current.click()
                }
              }}
            >
              Save Permission
            </Button>
            </>
          }
        >
          {innerContent}
        </Layout>
      ) : (
        innerContent
      )
    }

    return renderView(
      <>
        <Form
          onSubmit={handleSubmit((data) => {
            const commonParams = {
              submitForm,
              data,
              setShowOverlay,
              setError,
              id: permissionId,
              name: 'Permission',
              successNav: '/permissions',
              extraData: {
                filterValues: relatedFilterValuesRef.current,
                sections: relationalSections,
              },
            }

            return props.modalConfig !== undefined
              ? prepareSubmitFormFromModal({
                  ...commonParams,
                  modalConfig: props.modalConfig,
                })
              : prepareSubmitForm({
                  ...commonParams,
                  navigate: navigate,
                })
          })}
          id='main-form'
        >
          <InputWrapper colAttributes={colAttributes} label='Name' name='name' errors={errors}>
            <Form.Control type='text' {...register('name')} />
          </InputWrapper>

          <InputWrapper colAttributes={colAttributes} label='Active' name='active' errors={errors}>
            <Form.Select {...register('active')}>
              <option key={0} value={1}>
                Yes
              </option>
              <option key={1} value={0}>
                No
              </option>
            </Form.Select>
          </InputWrapper>

          <Button
            type='submit'
            name='save'
            ref={(ref as any) ?? submitRef}
            className='d-none'
          ></Button>
        </Form>

        {props.modalConfig === undefined && (
          <AddRemoveTableSortable
            getAllRowsUrl={apiUrls.permissionsToSections + '?id=' + id}
            addRemoveRowUrl={apiUrls.permissionsToSections}
            relationalFieldName='section_id'
            toggleActiveUrl={apiUrls.sections}
            maxTableHeight='600px'
            entityNameSingular='Section'
            entityNamePlural='Sections'
            wrapperCssClasses='mb-5'
            setShowOverlay={setShowOverlay}
            editFormComponent={AddSection}
            invertInitialSelection={isCreate}
            showSelectAll={true}
            showDeselectAll={true}
            onAddItem={
              !isCreate
                ? undefined
                : (entity: Entity) => {
                    setRelationalSections((prevState) => [...prevState, entity])
                    return true
                  }
            }
            onRemoveItem={
              !isCreate
                ? undefined
                : (entity: Entity) => {
                    setRelationalSections((prevState) =>
                      prevState.filter((r) => r.id !== entity.id),
                    )
                    return true
                  }
            }
            onAddAllItems={isCreate ? undefined : async (entities: Entity[]) =>{
              return await addAllSections({permissionId: parseInt(id!)})
            }}
            onRemoveAllItems={isCreate ? undefined : async (entities: Entity[], setIncludedRows: React.Dispatch<React.SetStateAction<Entity[]>>) =>{
              const ret = await removeAllSections({permissionId: parseInt(id!)})
              if(ret){
                setIncludedRows([])
              }
              return ret
            }}

          />
        )}

        <h4 className='mt-5'>Filter Permissions</h4>

        <Row>
          <Col xs={12} md={6} lg={4}>
            <Select
              options={flattenedFilters}
              placeholder={'Select Filter...'}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id}
              isClearable={true}
              onChange={(value) => {
                setSelectedFilter(value)
              }}
            />
          </Col>
       </Row>
       
       <Row className='mt-3'>
        <Col xs={12} md={6} lg={4}>
          <Form.Check
            type="checkbox"
            id="recently-synced-only"
            label="Recently Synced Filter Values only"
            checked={recentlySyncedFilterValues}
            onChange={(e) => setRecentlySyncedFilterValues(e.target.checked)}
          />
        </Col>
      </Row>

        {props.modalConfig === undefined &&
          (isCreate ? filters : paginatedFilters).map((filter, index) => (
            <div
            key={filter.id}
            style={{
              display: !isCreate || currentPage === index + 1 ? 'block' : 'none'
            }}
          >
            <RecursiveFilterBlock
              key={filter.id}
              filter={filter}
              id={!isCreate ? parseInt(id!) : undefined}
              isCreate={isCreate}
              apiUrls={apiUrls}
              setShowOverlay={setShowOverlay}
              selectedFilter={selectedFilter}
              selectedFilterAndChildrenIdsFlat={selectedFilterAndChildrenIdsFlat}
              showRecentOnly={recentlySyncedFilterValues}
              onAddItem={
                !isCreate
                ? undefined
                : (entity: Entity) => {
                  relatedFilterValuesRef.current.push(entity)
                  return true
                }
              }
              onRemoveItem={
                !isCreate
                  ? undefined
                  : (entity: Entity) => {
                      const index = relatedFilterValuesRef.current.findIndex(e => e.id === entity.id);
                      if (index !== -1) {
                        relatedFilterValuesRef.current.splice(index, 1);
                      }
                      return true
                    }
              }
              onAddAllItemsInvertedInitialSelection={
                isCreate
                  ? (entities: Entity[]) => {
                    const existingIds = new Set(relatedFilterValuesRef.current.map((e) => e.id))
                    const newEntities = entities.filter((e) => !existingIds.has(e.id))
              
                      relatedFilterValuesRef.current.push(...newEntities)
              
                      return true
                    }
                  : undefined
              }
            />
            </div>
          ))}

          {(!isCreate || selectedFilter === null) && (
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} />
          )}
      </>,
    )
  },
)

export default AddPermission
